import React, { useReducer } from 'react';
import clsx from 'clsx';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import GitInfo from 'react-git-info/macro';
import { useCookies } from 'react-cookie';
import { BrowserRouter as Router, Switch as RouterSwitch, useLocation } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import TMAppBar from '../lib/AppBar/TMAppBar';
import { ROUTES } from './routes';
import useAuth from '../hooks/useAuth';
import SetLocalStorageVars from '../components/SetLocalStorageVars';
import { isTokenValid } from '../util/token';
import useDrawer from '../hooks/useDrawer';
import TMDialog from '../lib/Dialog/TMDialog';
import reducer from '../util/reducer';
import useIntl from '../hooks/useIntl';
import TMForm from '../lib/Form/TMForm';
import client from '../feathers';
import axios from "axios";
import CookieBanner from '../components/CookieBanner';
import ItsmeConfirmPage from './ItsmeConfirmPage';

const initialState = {
  openEula: true,
  eulaAccepted: false,
  latestEulaId: undefined,
  loggedIn: false
};

const drawerWidth = '30%';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      fontFamily: 'Roboto',
    },
    appBar: {
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      height: '12vh',
    },
    appBarShift: {
      width: `calc(100vw - ${drawerWidth})`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: drawerWidth,
    },
    content: {
      flexGrow: 1,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginRight: 0,
      overflow: 'auto',
      height: '88vh',
    },
    contentShift: {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: drawerWidth,
    },
    gitInfo: {
      position: 'absolute',
      bottom: '10px',
      left: '10px',
      fontSize: '10px',
      padding: '4px',
      backgroundColor: theme.palette.secondary.dark,
      color: theme.palette.secondary.light,
    },
  })
);

const Layout = (props) => {
  const classes = useStyles();
  const location = useLocation();
  const { accessToken, user } = useAuth();
  const { showDrawer, toggleDrawer } = useDrawer();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { translate } = useIntl();
  const gitInfo = GitInfo();
  const [cookies] = useCookies(['cookieAccepted']);

  const showCookieBanner = cookies.cookieAccepted === undefined;

  const helpClicked = () => toggleDrawer();

  const handleChange = (e) => {
    dispatch({
      payload: {
        [e.target.name]: e.target.checked,
        latestEulaId: user.eulaToSign.id,
      },
    });
  };

  const handleAddSavedEula = async () => {
    await client
      .service('users')
      .patch(user.id, { eulaId: state.latestEulaId });
    window.location.reload();
  };

  const logout = async () => {
    // dispatch({ payload: { openEula: false } });
    window.addToDataLayer('eula-not-accepted', 'logout');
    localStorage.clear();
    sessionStorage.clear();
    // window.location.reload();
    // call logout
    try {
      let {token} = window.walletApi._getWalletInfo();
      await axios.post(process.env.REACT_APP_OAUTH_REVOKE_ENDPOINT, {token, client_id: process.env.REACT_APP_OAUTH_CLIENT_ID})

    } catch (error) {
      console.error(error);
    } finally {
      //  props.resetAuthentication();
      //  dispatch({ payload: { openEula: false, loggedOut: true }});
    }
    //  window.location.reload();
  }

  const login = () => {
    props.resetAuthentication();
    //  dispatch({ payload: { openEula: false, loggedIn: true }});
  }

  const getGitInfo = () => {
    return ["production", "uat", "cmacgm", "hpl"].includes(process.env.REACT_APP_NODE_ENV)
      ? ( <React.Fragment /> ) 
      : ( <div className={classes.gitInfo}>{gitInfo.branch} ({gitInfo.commit.shortHash}: {gitInfo.commit.message})</div>)
  };

  if (window.location.pathname === "/itsme-confirm") {
    return <ItsmeConfirmPage/>
  }

  if (!isTokenValid()) {
    return <SetLocalStorageVars login={login}>;
      {showCookieBanner && <CookieBanner position='right' />}
      {getGitInfo()}
      </SetLocalStorageVars>;
  }

  // SCRDEV-907 Check here whether user has to sign a new EULA
  if (user && user.hasOwnProperty('eulaToSign')) {
    return (
      <TMDialog
        key="sign-eula"
        title={translate('login.accept.eula')}
        dialogOpen={state.openEula}
        handleDialogClose={logout}
        maxWidth="sm"
        showBottomClose={false}
      >
        <p>
          {translate('eula.intro.1')}
          <br />
          {translate('eula.intro.2')}
        </p>
        <TMForm
          object={state}
          handleChange={handleChange}
          handleSubmit={handleAddSavedEula}
          submitLabel={translate('login.accept.eula.submit')}
          handleCancel={logout}
          cancelLabel={translate('general.cancel')}
          enableSubmitCondition={state.eulaAccepted}
          fields={[
            {
              field: 'eulaAccepted',
              label: translate('order.premium.label.eula'),
              componentType: Switch,
              fullWidth: true,
              required: true,
              placeholder: translate('order.premium.label.eula.accept'),
              helperText: (
                <a href={user.eulaToSign.url} target="_new">
                  {translate('order.premium.label.eula.read')}
                </a>
              ),
            },
          ]}
        />
        {getGitInfo()}
        {showCookieBanner && <CookieBanner />}
      </TMDialog>
    );
  }

  return (
    <div className={classes.container}>
      <Router>
        <TMAppBar
          user={user}
          isAuthenticated={!!accessToken}
          handleHelpClick={helpClicked}
          className={clsx(classes.appBar, showDrawer && classes.appBarShift)}
          logoPath="/tmining-logo-mix.png"
        />
        <div
          className={clsx(classes.content, showDrawer && classes.contentShift)}
        >
          {showCookieBanner && <CookieBanner />}
          <RouterSwitch>
            {ROUTES.map(({ path, component, isProtected }) => (
              <ProtectedRoute
                key={path}
                path={`/${path}`}
                component={component}
                isProtected={isProtected}
                exact
              />
            ))}
          </RouterSwitch>
          {getGitInfo()}
        </div>
      </Router>
    </div>
  );
};

export default Layout;
